import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ViewItem } from "../../Components/Views/ViewItem";
import { LogoView } from "../../Components/Views/LogoView";
import { controllers } from "../../Controllers/Controllers";
import { navigator } from "../../Controllers/Navigator";
import { REQUESTS_UNREAD_EVENT } from "../../DataTypes/contactRequests";
import './leftsidedrawer-styles.css'

type NavItem = {
    label: string;
    path: string;
    icon: React.ReactNode;
    exact?: boolean;
    children?: { label: string; path: string }[];
    badgeKey?: 'unreadReports' | 'unreadRequests';
    adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
    {
        label: "Outreach",
        path: "/outreach",
        exact: true,
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13" />
                <path d="M22 2 15 22 11 13 2 9 22 2Z" />
            </svg>
        )
    },
    {
        label: "Research",
        path: "/outreach/research",
        exact: true,
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.35-4.35" />
            </svg>
        )
    },
    {
        label: "Categories",
        path: "/outreach/categories",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41 12 22l-8-8V4h10l6.59 6.59a2 2 0 0 1 0 2.82Z" />
                <circle cx="7.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
            </svg>
        )
    },
    {
        label: "Report",
        path: "/outreach/report",
        exact: true,
        badgeKey: "unreadReports",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
        )
    },
    {
        label: "Requests",
        path: "/outreach/requests",
        exact: true,
        badgeKey: "unreadRequests",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
            </svg>
        )
    },
    {
        label: "Dashboard",
        path: "/dashboard",
        exact: true,
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="8" height="10" rx="1.5" />
                <rect x="13" y="3" width="8" height="6" rx="1.5" />
                <rect x="13" y="11" width="8" height="10" rx="1.5" />
                <rect x="3" y="15" width="8" height="6" rx="1.5" />
            </svg>
        )
    },
    {
        label: "Invoices",
        path: "/payroll",
        exact: true,
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2" />
                <circle cx="12" cy="12" r="2" />
                <path d="M6 12h.01" />
                <path d="M18 12h.01" />
            </svg>
        )
    },
    {
        label: "Mail",
        path: "/outreach/communications",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v16H4z" />
                <path d="M4 6 12 13 20 6" />
            </svg>
        ),
        children: [
            { label: "Compose", path: "/outreach/compose" },
            { label: "Campaign", path: "/outreach/compose/campaign" },
            { label: "Communications", path: "/outreach/communications" },
            { label: "All Inbox", path: "/outreach/inbox" },
            { label: "Sent", path: "/outreach/sent" },
            { label: "Templates", path: "/outreach/templates" },
        ]
    }
]

class LeftSideDrawerImpl extends ViewItem {
    state: any;
    props: any;
    flyoutCloseTimer: any = null;

    constructor(props: any) {
        super(props)
        this.state = {
            collapsed: controllers().auth.drawerCollapsed,
            unreadReports: 0,
            unreadRequests: 0,
            openFlyoutPath: null,
            flyoutPosition: null,
            mobileOpen: false,
        }
    }

    componentDidMount(): void {
        this.loadUnreadReportsCount()
        this.loadUnreadRequestsCount()
        window.addEventListener(REQUESTS_UNREAD_EVENT, this.handleUnreadRequestsEvent)
    }

    componentDidUpdate(prevProps: any): void {
        // Covers navigation that doesn't go through the links' own onClick
        // (e.g. the browser back/forward buttons) — the overlay shouldn't
        // still be covering the screen after the route's already changed.
        if (this.props.pathname !== prevProps.pathname && this.state.mobileOpen) {
            this.setState({ mobileOpen: false })
        }
    }

    componentWillUnmount(): void {
        if (this.flyoutCloseTimer) clearTimeout(this.flyoutCloseTimer)
        window.removeEventListener(REQUESTS_UNREAD_EVENT, this.handleUnreadRequestsEvent)
    }

    toggleMobileOpen = () => {
        this.setState((prev: any) => ({ mobileOpen: !prev.mobileOpen }))
    }

    closeMobile = () => {
        this.setState({ mobileOpen: false })
    }

    // The response envelope for this list endpoint isn't pinned down beyond
    // its pagination shape, so this defensively falls back to counting
    // whatever array key comes back before giving up and showing nothing.
    loadUnreadReportsCount = async () => {
        if (!controllers().auth.userToken) return
        const response = await controllers().report.listLogs({ is_read: false, per_page: 1 })
        if (!response || response.statusCode !== 200) return
        const data = response.data
        const count = data?.pagination?.total_count ?? (
            Array.isArray(data) ? data.length : (data?.entries || data?.logs || data?.status_logs || []).length
        )
        this.setState({ unreadReports: count })
    }

    loadUnreadRequestsCount = async () => {
        if (!controllers().auth.userToken || !controllers().auth.isAdmin) return
        const response = await controllers().outreach.getNewRequestLogs({ limit: 1 })
        if (!response || response.statusCode !== 200) return
        this.setState({ unreadRequests: response.data?.unread_count || 0 })
    }

    handleUnreadRequestsEvent = (e: Event) => {
        this.setState({ unreadRequests: (e as CustomEvent<number>).detail || 0 })
    }

    toggleCollapsed = () => {
        this.setState((prev: any) => {
            const collapsed = !prev.collapsed
            controllers().auth.setDrawerCollapsed(collapsed)
            return { collapsed, openFlyoutPath: null, flyoutPosition: null }
        })
    }

    // Collapsed mode hides the icon labels, so a nav item's children
    // (e.g. Mail's Inbox/Sent/Compose) have nowhere to render inline — this
    // opens them as a flyout instead, positioned from the hovered icon's
    // actual screen position rather than plain CSS :hover, since .lsd-nav
    // scrolls (overflow-y) and CSS forces overflow-x to clip along with it,
    // which would cut the flyout off if it were just absolutely positioned.
    handleGroupMouseEnter = (item: NavItem, target: HTMLElement) => {
        if (!this.state.collapsed || !item.children) return
        if (this.flyoutCloseTimer) {
            clearTimeout(this.flyoutCloseTimer)
            this.flyoutCloseTimer = null
        }
        const rect = target.getBoundingClientRect()
        this.setState({
            openFlyoutPath: item.path,
            flyoutPosition: { top: rect.top, left: rect.right + 8 },
        })
    }

    // Delayed close, cancellable — without it, the mouse crossing the gap
    // between the icon and the flyout (they're not DOM-adjacent, since the
    // flyout is positioned independently) would close it before it's reached.
    scheduleFlyoutClose = () => {
        this.flyoutCloseTimer = setTimeout(() => {
            this.setState({ openFlyoutPath: null, flyoutPosition: null })
        }, 150)
    }

    cancelFlyoutClose = () => {
        if (this.flyoutCloseTimer) {
            clearTimeout(this.flyoutCloseTimer)
            this.flyoutCloseTimer = null
        }
    }

    closeFlyoutNow = () => {
        this.cancelFlyoutClose()
        this.setState({ openFlyoutPath: null, flyoutPosition: null })
    }

    render(){
        const { collapsed, unreadReports, unreadRequests, openFlyoutPath, flyoutPosition, mobileOpen } = this.state
        const { pathname } = this.props
        const flyoutItem = collapsed ? NAV_ITEMS.find(i => i.path === openFlyoutPath) : null
        const closeMobileAndNavigate = () => { navigator().afterLocateFunctions(); this.closeMobile() }

        return (
            <>
            <button
                className="lsd-mobile-toggle"
                onClick={this.toggleMobileOpen}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
                {mobileOpen ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="M6 6 18 18" />
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M3 12h18" />
                        <path d="M3 18h18" />
                    </svg>
                )}
            </button>

            {mobileOpen && <div className="lsd-mobile-backdrop" onClick={this.closeMobile} />}

            <div className={`left-side-drawer ${collapsed ? 'lsd-collapsed' : ''}`}/>
            <div className={`left-side-drawer fixed-drawer ${collapsed ? 'lsd-collapsed' : ''} ${mobileOpen ? 'lsd-mobile-open' : ''}`}>
                <div className="lsd-header" onClick={() => navigator().navigate('/')}>
                    {collapsed ? (
                        <div className="lsd-logo-mark">W</div>
                    ) : (
                        <LogoView />
                    )}
                </div>

                <nav className="lsd-nav">
                    {NAV_ITEMS.filter(item => !item.adminOnly || controllers().auth.isAdmin).map(item => (
                        <div
                            key={item.path}
                            className="lsd-nav-group"
                            onMouseEnter={(e) => this.handleGroupMouseEnter(item, e.currentTarget)}
                            onMouseLeave={() => item.children && this.scheduleFlyoutClose()}
                        >
                            <NavLink
                                to={item.path}
                                end={item.exact}
                                onClick={closeMobileAndNavigate}
                                className={({ isActive }: { isActive: boolean }) => {
                                    const childActive = item.children?.some(c => c.path === pathname) ?? false
                                    return `lsd-nav-item ${(isActive || childActive) ? 'lsd-nav-item-active' : ''}`
                                }}
                                title={collapsed ? item.label : undefined}
                            >
                                <span className="lsd-nav-icon">{item.icon}</span>
                                <span className="lsd-nav-label">{item.label}</span>
                                {item.badgeKey === 'unreadReports' && unreadReports > 0 && (
                                    <span className="lsd-nav-badge">{unreadReports > 99 ? '99+' : unreadReports}</span>
                                )}
                                {item.badgeKey === 'unreadRequests' && unreadRequests > 0 && (
                                    <span className="lsd-nav-badge">{unreadRequests > 99 ? '99+' : unreadRequests}</span>
                                )}
                            </NavLink>

                            {/* Rendered unconditionally now — CSS hides these when collapsed on
                                desktop (relying on the hover flyout instead) but forces them back
                                on mobile, where collapsed is a leftover desktop preference that
                                doesn't apply and there's no hover to open a flyout with anyway. */}
                            {item.children && (
                                <div className="lsd-nav-children">
                                    {item.children.map(child => (
                                        <NavLink
                                            key={child.path}
                                            to={child.path}
                                            end
                                            onClick={closeMobileAndNavigate}
                                            className={({ isActive }: { isActive: boolean }) => `lsd-nav-subitem ${isActive ? 'lsd-nav-item-active' : ''}`}
                                        >
                                            {child.label}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                {flyoutItem && flyoutItem.children && flyoutPosition && (
                    <div
                        className="lsd-nav-flyout"
                        style={{ top: flyoutPosition.top, left: flyoutPosition.left }}
                        onMouseEnter={this.cancelFlyoutClose}
                        onMouseLeave={this.scheduleFlyoutClose}
                    >
                        <div className="lsd-nav-flyout-label">{flyoutItem.label}</div>
                        {flyoutItem.children.map(child => (
                            <NavLink
                                key={child.path}
                                to={child.path}
                                end
                                onClick={() => { navigator().afterLocateFunctions(); this.closeFlyoutNow() }}
                                className={({ isActive }: { isActive: boolean }) => `lsd-nav-subitem ${isActive ? 'lsd-nav-item-active' : ''}`}
                            >
                                {child.label}
                            </NavLink>
                        ))}
                    </div>
                )}

                <button
                    className="lsd-collapse-btn"
                    onClick={this.toggleCollapsed}
                    aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lsd-collapse-icon">
                        <path d="M15 18 9 12 15 6" />
                    </svg>
                    <span className="lsd-nav-label">Collapse</span>
                </button>
            </div>
            </>
        )
    }
}

// NavLink already tracks the current route on its own for a single path,
// but the Mail group's "active" state depends on ANY of its children's
// paths too — that needs the current pathname up here, which a class
// component can't get from useLocation() directly.
function LeftSideDrawer(props: any) {
    const { pathname } = useLocation()
    return <LeftSideDrawerImpl {...props} pathname={pathname} />
}

export { LeftSideDrawer }
